import { Box, Button, Tooltip, Typography } from "@mui/material";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import withStyles from "@mui/styles/withStyles";
import { ReactComponent as InfoCircleIcon } from "components/Icons/info-circle.svg";
import { OLTheme } from "contexts/Theme/ThemeProvider";
import { useMessage } from "hooks/intl";
import { useState } from "react";
import { SchemaAttribute } from "types/customEvents";
import { countSchemaObject } from "../SchemaImportList/utils";

const TooltipButton = withStyles((theme: OLTheme) => ({
  root: {
    background: theme.gray100,
    borderRadius: theme.spacing(2),
    flexWrap: "nowrap",
    margin: theme.spacing(2),
    padding: theme.spacing(2, 4),
    textTransform: "none",
  },
}))(Button);

const ItemsStyles = makeStyles((theme: OLTheme) =>
  createStyles({
    chipsName: {
      textOverflow: "ellipsis",
      maxWidth: 180,
      overflow: "hidden",
    },
    tooltipItem: {
      color: theme.gray500,
      display: "flex",
      fontSize: 12,
      lineHeight: "20px",
    },
    icon: {
      color: theme.gray400,
      fontSize: 13,
      marginLeft: theme.spacing(2),
    },
    toggleSeeAll: {
      color: theme.gray400,
      cursor: "pointer",
    },
    breakWord: {
      wordBreak: "break-word",
    },
    tooltipName: {
      color: theme.black,
      fontSize: 14,
      fontWeight: 500,
      lineHeight: "24px",
      marginBottom: theme.spacing(2),
    },
    objectsCount: {
      width: 20,
      height: 20,
      minWidth: 20,
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      borderRadius: "4px",
      color: theme.white00,
      backgroundColor: theme.primary700,
      marginLeft: theme.spacing(2),
      fontSize: 14,
    },
  }),
);

const SchemaTooltip = withStyles((theme: OLTheme) => ({
  tooltip: {
    backgroundColor: theme.white00,
    boxShadow: theme.shadows[1],
    display: "flex",
    flexDirection: "column",
    lineHeight: "22px",
    padding: theme.spacing(8),
  },
}))(Tooltip);

export function AttributeChip({ items }: { items: SchemaAttribute[] }) {
  const classes = ItemsStyles();
  const { message } = useMessage();
  const [seeAll, setSeeAll] = useState(false);

  return (
    <>
      {items.slice(0, seeAll ? items.length : 2).map((item) => {
        const isComplexAttribute =
          item.type === "object" || item.type === "collection";
        const objectsCount = isComplexAttribute
          ? countSchemaObject(item, 0)
          : 0;

        return (
          <SchemaTooltip
            title={
              <>
                <Typography className={classes.tooltipName}>
                  {item.description}
                </Typography>
                {[
                  { label: "name", value: item.description },
                  { label: "type", value: item.type },
                  { label: "systemIdentifier", value: item.name },
                  ...(isComplexAttribute
                    ? [
                        {
                          label: "numberOfObjects",
                          value: objectsCount,
                        },
                      ]
                    : []),
                ].map(
                  (
                    item: { label: string; value?: string | number },
                    index: number,
                  ) => (
                    <div className={classes.tooltipItem} key={index}>
                      <div>{message({ id: item.label })}:&nbsp;</div>
                      <Box className={classes.breakWord}>
                        {" "}
                        {typeof item.value === "number"
                          ? item.value
                          : item.value || "-"}
                      </Box>
                    </div>
                  ),
                )}
              </>
            }
            key={item.name}
          >
            <TooltipButton>
              <span className={classes.chipsName}>
                {item.description || item.name}
              </span>
              {isComplexAttribute && (
                <div className={classes.objectsCount}>{objectsCount}</div>
              )}
              <InfoCircleIcon className={classes.icon} />
            </TooltipButton>
          </SchemaTooltip>
        );
      })}
      {items.length > 2 && (
        <span
          className={classes.toggleSeeAll}
          onClick={(e) => {
            e.stopPropagation();
            setSeeAll(!seeAll);
          }}
        >
          {message({ id: seeAll ? "hide" : "seeAll" }).toLowerCase()}
        </span>
      )}
    </>
  );
}
